import React, {
  useCallback, useEffect, useLayoutEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  DEFAULT_SAVED_VIEW_NAME,
  SAVED_VIEW_COPY,
  validateSavedViewName,
} from '../../containers/records/shared/savedViews/savedViewFilters';
import './savedViewsBar.scss';

// ENG-3551. Popovers here are plain positioned panels rendered next to their
// trigger (not a body portal), so Tab order runs trigger → panel → next control,
// and Escape / outside click close them and return focus to the trigger.
const POPOVER_MAX_WIDTH = 320;
const VIEWPORT_GUTTER = 16;

// Panels open to the right of their trigger unless that would cross the viewport
// edge (a chip at the end of a row); then they align to the trigger's right edge.
export const popoverAlignClass = (element) => {
  const rect = element?.getBoundingClientRect?.();
  if (!rect || typeof window === 'undefined') return '';
  return rect.left + POPOVER_MAX_WIDTH > window.innerWidth - VIEWPORT_GUTTER ? ' saved-views-popover--end' : '';
};

export const useDismissablePopover = ({ open, onDismiss, containerRef }) => {
  const dismissRef = useRef(onDismiss);
  useLayoutEffect(() => {
    dismissRef.current = onDismiss;
  });

  useEffect(() => {
    if (!open) return undefined;
    const handlePointer = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        dismissRef.current('outside');
      }
    };
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      dismissRef.current('escape');
    };
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('touchstart', handlePointer, { passive: true });
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('touchstart', handlePointer, { passive: true });
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, containerRef]);
};

// The name form shared by Save view, Save as new and Rename: one labelled input,
// explicit Cancel / Save, Enter submits, Escape cancels. The parent's onSubmit
// resolves { ok, error }; the form closes only on ok.
export const SavedViewNameForm = ({
  title, initialName, saving, error: externalError, showNote, submitLabel, onSubmit, onCancel,
}) => {
  const [name, setName] = useState(initialName);
  const [error, setError] = useState(externalError || null);
  const inputRef = useRef(null);
  const inputId = useRef(`saved-view-name-${Math.random().toString(36).slice(2)}`).current;

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, []);

  useEffect(() => {
    if (externalError) setError(externalError);
  }, [externalError]);

  const submit = async () => {
    if (saving) return;
    const validation = validateSavedViewName(name);
    if (validation) {
      setError(validation);
      return;
    }
    setError(null);
    const result = await onSubmit(name);
    if (result && !result.ok && result.error) setError(result.error);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      submit();
    }
  };

  return (
    <div className="saved-views-form">
      <p className="saved-views-form__title">{title}</p>
      <label className="saved-views-form__label" htmlFor={inputId}>{SAVED_VIEW_COPY.viewName}</label>
      <input
        id={inputId}
        ref={inputRef}
        className="saved-views-form__input"
        type="text"
        value={name}
        autoComplete="off"
        aria-invalid={error ? 'true' : 'false'}
        aria-describedby={error ? `${inputId}-error` : undefined}
        onChange={(event) => setName(event.target.value)}
        onKeyDown={handleKeyDown}
      />
      {error && (
        <p id={`${inputId}-error`} className="saved-views-form__error" role="alert">{error}</p>
      )}
      {showNote && <p className="saved-views-form__note">{SAVED_VIEW_COPY.excludedNote}</p>}
      <div className="saved-views-form__actions">
        <button type="button" className="saved-views-btn" onClick={() => onCancel('button')}>
          {SAVED_VIEW_COPY.cancel}
        </button>
        <button
          type="button"
          className="saved-views-btn saved-views-btn--primary"
          onClick={submit}
          disabled={saving}
        >
          {saving ? SAVED_VIEW_COPY.saving : submitLabel}
        </button>
      </div>
    </div>
  );
};

SavedViewNameForm.propTypes = {
  title: PropTypes.string.isRequired,
  initialName: PropTypes.string,
  saving: PropTypes.bool,
  error: PropTypes.string,
  showNote: PropTypes.bool,
  submitLabel: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

SavedViewNameForm.defaultProps = {
  initialName: DEFAULT_SAVED_VIEW_NAME,
  saving: false,
  error: null,
  showNote: false,
  submitLabel: SAVED_VIEW_COPY.save,
};

// `Save view` (no selection) or `Save as new` (Modified group). The parent decides
// visibility and supplies onSave(name) → Promise<{ ok, error }>; it reads the
// current filters at submit time.
const SaveViewButton = ({
  label, defaultName, saving, error, onSave, onCancel, onOpen, variant,
}) => {
  const [open, setOpen] = useState(false);
  const [alignClass, setAlignClass] = useState('');
  const containerRef = useRef(null);
  const triggerRef = useRef(null);

  const close = useCallback((reason) => {
    setOpen(false);
    onCancel(reason);
    if (reason !== 'outside' && triggerRef.current) triggerRef.current.focus();
  }, [onCancel]);

  useDismissablePopover({ open, onDismiss: close, containerRef });

  const toggle = () => {
    if (open) {
      close('button');
      return;
    }
    setAlignClass(popoverAlignClass(containerRef.current));
    setOpen(true);
    onOpen();
  };

  const submit = async (name) => {
    const result = await onSave(name);
    if (result?.ok) {
      setOpen(false);
      // The parent may unmount this button once the new view is selected; it then
      // owns focus (SavedViewsBar focusRequest).
      if (triggerRef.current?.isConnected) triggerRef.current.focus();
    }
    return result;
  };

  return (
    <span className="saved-views-popover-anchor" ref={containerRef}>
      <button
        type="button"
        ref={triggerRef}
        className={variant === 'link' ? 'saved-views-bar__action' : 'saved-views-save-btn'}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={toggle}
      >
        {label}
      </button>
      {open && (
        <div className={`saved-views-popover${alignClass}`} role="dialog" aria-label={label}>
          <SavedViewNameForm
            title={label}
            initialName={defaultName}
            saving={saving}
            error={error}
            showNote
            onSubmit={submit}
            onCancel={close}
          />
        </div>
      )}
    </span>
  );
};

SaveViewButton.propTypes = {
  label: PropTypes.string,
  defaultName: PropTypes.string,
  saving: PropTypes.bool,
  error: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  onOpen: PropTypes.func,
  variant: PropTypes.oneOf(['button', 'link']),
};

SaveViewButton.defaultProps = {
  label: SAVED_VIEW_COPY.saveView,
  defaultName: DEFAULT_SAVED_VIEW_NAME,
  saving: false,
  error: null,
  onCancel: () => {},
  onOpen: () => {},
  variant: 'button',
};

export default SaveViewButton;
